import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormTextareaComponent from 'webapp/components/form/textarea';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Group from 'webapp/models-custom/group';

export default class GroupPopupEditComponent extends PopupInnerModalComponent<PopupData.Group.Edit> {
	@service('model-manager/groups') declare mmGroupsService: Service.ModelManager.Groups;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('account') declare accountService: Service.Account;

	@tracked override submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
	}

	get model(): Group {
		return this.popupData.model;
	}

	get isGroupNameDuplicatedOrSameAsCompany(): boolean {
		const lowercasedName = this.model.name?.toLowerCase(),
			id = this.model.id;

		const isDuplicateGroupName = this.mmGroupsService.groupModels
			.filter((group) => group.id !== id)
			.some((group) => group.name.toLowerCase() === lowercasedName);

		const isSameAsCompanyName = this.accountService.company.toLowerCase() === lowercasedName;

		return isDuplicateGroupName || isSameAsCompanyName;
	}

	get isNew(): boolean {
		return this.model.isNew;
	}

	get isSaveDisabled(): boolean {
		return !(this.model.name || '').trim() || this.isGroupNameDuplicatedOrSameAsCompany;
	}

	@action
	save() {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;

		this.model
			.save()
			.then(() => {
				this.popupData.onSave(this.model);

				this.infolayerService.ok('Team added!');

				this.close();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	closePopup(): void {
		if (!this.submitInProgress) {
			this.close();
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Plus" @size="lg" />
			New group

		</div>

		<div class="content">
			<div class="row input">
				<label>Group name</label>
				<div class="right">
					<FormInputComponent
						@value={{this.model.name}}
						placeholder="Enter group name (e.g. Marketing, Product, Sales, Finance, etc.)"
						@hideFloatLabel={{true}}
						@autofocus={{this.model.isNew}}
						disabled={{this.submitInProgress}}
						maxlength="50"
						required={{true}}
					/>
					{{#if this.isGroupNameDuplicatedOrSameAsCompany}}
						<div class="error">
							Group name is already taken or is the same as your company name
						</div>
					{{/if}}
				</div>
			</div>

			<div class="row input">
				<label>Description <span class="optional">(optional)</span></label>
				<div class="right">
					<FormTextareaComponent
						name="description"
						disabled={{this.submitInProgress}}
						placeholder="Add description"
						maxlength="255"
						@value={{this.model.description}}
						@hideFloatLabel={{true}}
					/>
				</div>
			</div>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Create"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSaveDisabled}}
					/>
				</div>
			</div>

		</div>
	</template>
}
